import { createSlice } from '@reduxjs/toolkit';
import { APIError, AuthState, User } from './types';
import {
  forgotPassword,
  getUser,
  loginUser,
  registerUser,
  verifyUser,
  resetPassword,
  updateUserOnboarding,
  socialLogin,
} from './userActions';
import {
  updateUserProfile,
  updateMarketingEmails,
} from '../settings/settingsActions';
import { destroyCookie, parseCookies } from 'nookies';
import { TypeOfUser } from 'src/utils';
import { normalizeDisplayLanguage } from 'data/languages';

function withDisplayLanguage(user: User): User {
  return { ...user, language: normalizeDisplayLanguage(user.language) };
}

const cookies = parseCookies();

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  isLoggedIn: !!cookies.accessToken,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isLoggedIn = false;
      destroyCookie(null, 'accessToken');
      destroyCookie(null, 'refreshToken');
    },
  },
  extraReducers: (builder) => {
    // Register user
    builder.addCase(registerUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(registerUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = withDisplayLanguage(action.payload.user);
      state.isLoggedIn = true;
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload?.detail === 'string'
          ? action.payload.detail
          : 'Register failed';
    });

    // Login user
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = withDisplayLanguage(action.payload.user);
      state.isLoggedIn = true;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload?.detail === 'string'
          ? action.payload.detail
          : 'Login failed';
    });

    // Get User
    builder
      .addCase(getUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.user = withDisplayLanguage(action.payload);
        state.loading = false;
        const role = action.payload.preferences;

        if (role === 'Real Estate Investors')
          state.user.role = TypeOfUser.REAL_ESTATE;
        if (role === 'International Buyer')
          state.user.role = TypeOfUser.INTER_BUYER;
        if (role === 'Retirees and Down-sizers')
          state.user.role = TypeOfUser.RETIREES;
        if (role === 'First time buyer')
          state.user.role = TypeOfUser.FIRST_TIME;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.error =
          typeof action.payload?.detail === 'string'
            ? action.payload.detail
            : 'Failed to load user data';
        state.loading = false;
      });

    // Verify User
    builder.addCase(verifyUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyUser.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(verifyUser.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload?.detail === 'string'
          ? action.payload.detail
          : 'Verification failed';
    });

    // Forgot Password
    builder.addCase(forgotPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(forgotPassword.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload?.detail === 'string'
          ? action.payload.detail
          : 'Failed to send reset instructions';
    });

    // Reset Password
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload?.detail === 'string'
          ? action.payload.detail
          : 'Failed to reset password';
    });
    builder.addCase(updateUserOnboarding.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserOnboarding.fulfilled, (state, action) => {
      state.loading = false;
      state.user = { ...state.user, ...action.payload };
    });
    builder.addCase(updateUserOnboarding.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload?.detail === 'string'
          ? action.payload.detail
          : 'Failed to reset password';
    });

    // Update user profile (from settings)
    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      state.user = { ...state.user, ...action.payload };
    });
    builder.addCase(updateMarketingEmails.fulfilled, (state, action) => {
      state.user = { ...state.user, ...action.payload };
    });

    //social login
    builder.addCase(socialLogin.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(socialLogin.fulfilled, (state, action) => {
      state.loading = false;
      state.user = withDisplayLanguage(action.payload.user);
      state.isLoggedIn = true;
    });
    builder.addCase(socialLogin.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof (action.payload as APIError)?.detail === 'string'
          ? ((action.payload as APIError).detail as string)
          : 'Social login failed';
    });
  },
});

export const { logout } = userSlice.actions;
export default userSlice.reducer;
